import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FloatingBubbles from '../components/FloatingBubbles';
import Avatar from '../components/Avatar';
import axios from 'axios';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function Messages() {
  const { conversationId } = useParams();
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const isMobile = useIsMobile();

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [activeConv, setActiveConv] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [showNewConv, setShowNewConv] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [mobileView, setMobileView] = useState('list');

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(res.data);
    } catch (err) { console.error(err); }
  }, [API, token]);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/messages/${convId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(res.data);
    } catch (err) { console.error(err); }
  }, [API, token]);

  useEffect(() => { if (token) fetchConversations(); }, [fetchConversations, token]);

  useEffect(() => {
    if (conversationId) {
      setActiveConv(parseInt(conversationId));
      fetchMessages(conversationId);
      if (isMobile) setMobileView('chat');
    }
  }, [conversationId, fetchMessages, isMobile]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!activeConv) return;
    const interval = setInterval(() => fetchMessages(activeConv), 3000);
    return () => clearInterval(interval);
  }, [activeConv, fetchMessages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!content.trim() || !activeConv) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/${activeConv}/messages`, { content }, { headers: { Authorization: `Bearer ${token}` } });
      setContent('');
      fetchMessages(activeConv);
      fetchConversations();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleNewConv = async (username) => {
    try {
      const res = await axios.post(`${API}/messages/${username}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setShowNewConv(false);
      setNewUsername('');
      setSearchResults([]);
      await fetchConversations();
      navigate(`/messages/${res.data.id}`);
      if (isMobile) setMobileView('chat');
    } catch (err) { console.error(err); }
  };

  const handleSearch = async (q) => {
    setNewUsername(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/users/search/users?q=${q}`);
      setSearchResults(res.data.filter(u => u.username !== user.username));
    } catch (err) { console.error(err); }
  };

  const handleSelectConv = (convId) => {
    navigate(`/messages/${convId}`);
    if (isMobile) setMobileView('chat');
  };

  const handleBack = () => {
    setMobileView('list');
    setActiveConv(null);
    setMessages([]);
    navigate('/messages');
  };

  const getOtherUser = (conv) => conv.user1.username === user.username ? conv.user2 : conv.user1;
  const activeConvData = conversations.find(c => c.id === activeConv);
  const otherUser = activeConvData ? getOtherUser(activeConvData) : null;

  if (!user) return (
    <div style={{ textAlign: 'center', padding: '80px', color: 'var(--muted)' }}>
      <Link to="/login" style={{ color: 'var(--blue)', fontWeight: 600 }}>Connecte toi</Link> pour accéder aux messages.
    </div>
  );

  const showSidebar = !isMobile || mobileView === 'list';
  const showChat = !isMobile || mobileView === 'chat';

  return (
    <>
      <FloatingBubbles />
      <div style={{ position: 'relative', zIndex: 1, height: 'calc(100vh - 64px)', display: 'flex', overflow: 'hidden' }}>

        {/* ── SIDEBAR ───────────────────────────────────── */}
        {showSidebar && (
          <div style={{
            width: isMobile ? '100%' : '320px',
            flexShrink: 0,
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(12px)',
            borderRight: isMobile ? 'none' : '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--dark)' }}>Messages</h2>
                <button onClick={() => setShowNewConv(!showNewConv)} style={{ background: 'var(--blue)', color: '#fff', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(43,108,176,0.3)', flexShrink: 0 }}>+</button>
              </div>
              {showNewConv && (
                <div style={{ position: 'relative' }}>
                  <input value={newUsername} onChange={e => handleSearch(e.target.value)} placeholder="Cherche un membre..." autoFocus
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--blue-border)', borderRadius: '12px', fontSize: '13px', outline: 'none', background: '#FAFCFF', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                  />
                  {searchResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', marginTop: '4px', boxShadow: 'var(--shadow-hover)', zIndex: 10, overflow: 'hidden' }}>
                      {searchResults.map(u => (
                        <div key={u.id} onClick={() => handleNewConv(u.username)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Avatar user={u} size={32} fontSize={11} />
                          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark)' }}>{u.username}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {conversations.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--muted)' }}>
                  <p style={{ fontSize: '14px' }}>Aucune conversation</p>
                  <p style={{ fontSize: '12px', marginTop: '6px' }}>Clique sur + pour commencer</p>
                </div>
              ) : conversations.map(conv => {
                const other = getOtherUser(conv);
                const lastMsg = conv.messages?.[conv.messages.length - 1];
                const isActive = conv.id === activeConv;
                return (
                  <div key={conv.id} onClick={() => handleSelectConv(conv.id)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '14px 16px' : '14px 20px', cursor: 'pointer', background: isActive ? 'var(--blue-bg)' : 'transparent', borderLeft: isActive ? `3px solid var(--blue)` : '3px solid transparent', transition: 'all .2s' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Avatar user={other} size={isMobile ? 48 : 44} fontSize={15} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dark)', marginBottom: '2px' }}>{other.username}</div>
                      {lastMsg && (
                        <div style={{ fontSize: '12px', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lastMsg.sender.username === user.username ? 'Toi: ' : ''}{lastMsg.content}
                        </div>
                      )}
                    </div>
                    {isMobile && <span style={{ fontSize: '20px', color: 'var(--muted)' }}>›</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── CHAT ──────────────────────────────────────── */}
        {showChat && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {activeConv && otherUser ? (
              <>
                {/* Header */}
                <div style={{ padding: isMobile ? '12px 16px' : '16px 24px', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {isMobile && (
                    <button onClick={handleBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '26px', color: 'var(--blue)', padding: '0', lineHeight: 1, flexShrink: 0 }}>‹</button>
                  )}
                  <Avatar user={otherUser} size={40} fontSize={14} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/profile/${otherUser.username}`} style={{ fontSize: '15px', fontWeight: 700, color: 'var(--dark)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {otherUser.username}
                    </Link>
                    {otherUser.bio && <div style={{ fontSize: '12px', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{otherUser.bio.slice(0, 40)}</div>}
                  </div>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 12px' : '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'var(--muted)', marginTop: '40px' }}>
                      <p style={{ fontSize: '14px' }}>Commence la conversation !</p>
                    </div>
                  ) : messages.map(msg => {
                    const isMe = msg.sender.username === user.username;
                    return (
                      <div key={msg.id} style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: '8px' }}>
                        {!isMe && <Avatar user={msg.sender} size={28} fontSize={10} />}
                        <div style={{ maxWidth: isMobile ? '80%' : '65%', padding: '10px 14px', borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: isMe ? 'var(--blue)' : 'rgba(255,255,255,0.95)', color: isMe ? '#fff' : 'var(--dark)', fontSize: '14px', lineHeight: 1.6, boxShadow: 'var(--shadow)', border: isMe ? 'none' : '1px solid var(--border)' }}>
                          {msg.content}
                          <div style={{ fontSize: '10px', color: isMe ? 'rgba(255,255,255,0.6)' : 'var(--muted)', marginTop: '4px', textAlign: 'right' }}>
                            {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div style={{ padding: isMobile ? '10px 12px' : '16px 24px', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderTop: '1px solid var(--border)' }}>
                  <form onSubmit={handleSend} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      value={content}
                      onChange={e => setContent(e.target.value)}
                      placeholder="Écris un message..."
                      style={{ flex: 1, padding: isMobile ? '11px 16px' : '12px 18px', border: '1.5px solid var(--blue-border)', borderRadius: '24px', fontSize: '14px', outline: 'none', background: '#FAFCFF', transition: 'border-color .2s', minWidth: 0 }}
                      onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                      onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                    />
                    <button type="submit" disabled={sending || !content.trim()} style={{ background: content.trim() ? 'var(--blue)' : 'var(--border)', color: content.trim() ? '#fff' : 'var(--muted)', border: 'none', width: '42px', height: '42px', borderRadius: '50%', cursor: content.trim() ? 'pointer' : 'default', fontSize: '18px', transition: 'all .2s', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      →
                    </button>
                  </form>
                </div>
              </>
            ) : (
              !isMobile && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ fontSize: '48px' }}>💬</div>
                  <p style={{ fontSize: '16px', fontWeight: 500, color: 'var(--dark)' }}>Tes messages</p>
                  <p style={{ fontSize: '14px', color: 'var(--muted)' }}>Sélectionne une conversation ou commence en une nouvelle.</p>
                  <button onClick={() => setShowNewConv(true)} style={{ background: 'var(--blue)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                    Nouveau message
                  </button>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </>
  );
}
